/**
 * The pinned top of the rail: the collapse toggle where it has always been,
 * then the Go box. Pinned so that, with many stars or a short viewport, the
 * stars scroll and the box never moves (nav spec §4.1).
 */
import React from 'react';

import { useUI } from '@/stores/useAppStore';
import { pinsSidebarCollapsed, showsUtilityRows } from '@shared/app-identity/appChrome';
import { useInstalledApp } from '@shared/app-identity/useInstalledApp';
import { Icons } from '@shared/components/ui';

import GoBox from './GoBox';
import RailEditToggle from './RailEditToggle';

interface SideNavHeaderProps {
  /** Visual collapsed flag (icon-only rail) from the parent's transition hook. */
  isCollapsed: boolean;
  /** Called after the Go box acts (e.g. so a host can close itself). */
  onNavigate?: () => void;
}

const SideNavHeader: React.FC<SideNavHeaderProps> = ({ isCollapsed, onNavigate }) => {
  const { isSidebarCollapsed, toggleSidebar } = useUI();
  const installedApp = useInstalledApp();
  // Nothing to reveal by widening a column that holds one avatar.
  const pinnedCollapsed = pinsSidebarCollapsed(installedApp);
  const showToggle = !pinnedCollapsed;
  const showGoBox = showsUtilityRows(installedApp);
  // Edit mode belongs to the browser rail, the only editable one; icon-only has no room.
  const showEditToggle = installedApp === null && !isCollapsed;
  // A pinned single-destination app has neither; render nothing rather than an empty band.
  if (!showToggle && !showGoBox) return null;

  return (
    <div className="px-2 pt-2">
      {showToggle && (
        <div className={`mb-1 flex items-center ${isCollapsed ? 'justify-center' : 'justify-end'}`}>
          {showEditToggle && <RailEditToggle />}
          <button
            type="button"
            onClick={toggleSidebar}
            className="hover:bg-sidebar-accent text-sidebar-foreground rounded-md p-2 transition-colors"
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icons.Menu size="sm" color="inherit" />
          </button>
        </div>
      )}

      {/* Apps with fewer than two destinations have nowhere to go from a box. */}
      {showGoBox && <GoBox isCollapsed={isCollapsed} {...(onNavigate && { onNavigate })} />}
    </div>
  );
};

export default SideNavHeader;
